import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AccountBalanceWalletOutlined,
  AssignmentOutlined,
  FactCheckOutlined,
  PaymentsOutlined,
} from "@mui/icons-material";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import SectionCard from "../SectionCard";
import StatusChip from "../StatusChip";
import { safely, subcontractApi } from "../../services/subcontractApi";
import { formatCurrencyWithUnit, formatNumber, formatCurrency } from "utils/formatters";


const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getField = (item, field) => item?.[field] ?? item?.[`${field.charAt(0).toUpperCase()}${field.slice(1)}`];
const percentOf = (value, total) =>
  total > 0 ? Math.max(0, Math.min(100, (toNumber(value) / total) * 100)) : 0;
const sumBy = (items = [], field) =>
  items.reduce((total, item) => total + toNumber(getField(item, field)), 0);
const statusEquals = (value, expected) =>
  String(value || "").toLocaleLowerCase("vi-VN") === expected.toLocaleLowerCase("vi-VN");
const getItemList = (profiles = []) => profiles.flatMap((profile) => profile?.items || []);
const optionalRequest = async (request, fallback) => {
  try {
    return await request();
  } catch {
    return fallback;
  }
};

function SummaryCard({ icon, title, rows }) {
  return (
    <Box className="subcontract-stat-summary-card">
      <Box className="subcontract-stat-summary-card__header">
        <span>{icon}</span>
        <Typography>{title}</Typography>
      </Box>
      <Box className="subcontract-stat-summary-card__rows">
        {rows.map((row) => (
          <Box key={row.label} className="subcontract-stat-row">
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SegmentedProgress({ total, segments }) {
  const visibleSegments = segments
    .map((segment) => ({
      ...segment,
      value: Math.max(toNumber(segment.value), 0),
      percent: percentOf(segment.value, total),
    }))
    .filter((segment) => segment.value > 0 && segment.percent > 0);

  return (
    <Box className="subcontract-stat-progress">
      <Box className="subcontract-stat-progress__track">
        {visibleSegments.map((segment) => (
          <span
            key={segment.label}
            style={{ width: `${segment.percent}%`, backgroundColor: segment.color }}
            title={`${segment.label}: ${formatNumber(segment.value)}`}
          />
        ))}
      </Box>
      <Box className="subcontract-stat-progress__legend">
        {segments
          .filter((segment) => toNumber(segment.value) > 0)
          .map((segment) => (
            <span key={segment.label}>
              <i style={{ backgroundColor: segment.color }} />
              {segment.label}
            </span>
          ))}
      </Box>
    </Box>
  );
}

function SubcontractStatCard({ item }) {
  const paymentTotal = toNumber(getField(item, "totalValue"));
  const paidAmount = toNumber(item.paidAmount);
  const remainingPayment = Math.max(paymentTotal - paidAmount, 0);
  const quantityTotal = toNumber(item.acceptedQuantity);

  return (
    <Box className="subcontract-stat-contract">
      <Box className="subcontract-stat-contract__header">
        <Box>
          <Typography className="subcontract-stat-contract__title">
            {getField(item, "subcontractNumber")}
          </Typography>
          <Typography className="subcontract-stat-contract__meta">
            {getField(item, "subcontractName")}
          </Typography>
        </Box>
        <StatusChip value={getField(item, "status")} />
      </Box>

      <Box className="subcontract-stat-contract__body">
        <Box className="subcontract-stat-block">
          <Box className="subcontract-stat-block__title">
            <PaymentsOutlined fontSize="small" />
            <Typography>Thông tin thanh toán</Typography>
          </Box>
          <Box className="subcontract-stat-lines">
            <Box><span>Giá trị giao khoán</span><strong>{formatCurrency(paymentTotal)}</strong></Box>
            <Box><span>Đã thanh toán</span><strong>{formatCurrency(paidAmount)}</strong></Box>
            <Box><span>Còn lại</span><strong>{formatCurrency(remainingPayment)}</strong></Box>
          </Box>
          <SegmentedProgress
            total={paymentTotal}
            segments={[
              { label: "Đã thanh toán", value: paidAmount, color: "#0b5cab" },
              { label: "Còn lại", value: remainingPayment, color: "#dfe6ed" },
            ]}
          />
        </Box>

        <Box className="subcontract-stat-block">
          <Box className="subcontract-stat-block__title">
            <FactCheckOutlined fontSize="small" />
            <Typography>Thông tin nghiệm thu</Typography>
          </Box>
          <Box className="subcontract-stat-lines">
            <Box><span>Khối lượng giao khoán</span><strong>{formatNumber(item.acceptedQuantity)}</strong></Box>
            <Box><span>Khối lượng nghiệm thu</span><strong>{formatNumber(item.subcontractQuantity)}</strong></Box>
            <Box><span>Khối lượng từ chối</span><strong>{formatNumber(item.rejectedQuantity)}</strong></Box>
            <Box><span>Khối lượng còn lại</span><strong>{formatNumber(item.remainingQuantity)}</strong></Box>
          </Box>
          <SegmentedProgress
            total={quantityTotal}
            segments={[
              { label: "Nghiệm thu", value: item.subcontractQuantity, color: "#27ae60" },
              { label: "Từ chối", value: item.rejectedQuantity, color: "#eb5757" },
              { label: "Còn lại", value: item.remainingQuantity, color: "#f2c94c" },
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function ContractStatisticsPanel({ contractId, allowedSubcontractIds = "all", mainContract: selectedMainContract }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const loadStatistics = useCallback(async () => {
    if (!contractId) {
      setData(null);
      return;
    }

    if (allowedSubcontractIds === null) {
      return;
    }

    setLoading(true);
    try {
      if (allowedSubcontractIds === "error" || allowedSubcontractIds === "none") {
        const [mainContract, mainPaymentsResult] = await Promise.all([
          selectedMainContract
            ? Promise.resolve(selectedMainContract)
            : safely(() => subcontractApi.getMainContract(contractId)),
          optionalRequest(() => subcontractApi.getMainContractPayments(contractId), []),
        ]);

        setData({
          mainContract,
          mainContractPaid: sumBy(mainPaymentsResult, "paymentAmount"),
          subcontracts: [],
        });
        setError("");
        return;
      }

      const subcontractParams = {
        pageSize: 10000,
        pageNumber: 1,
      };
      if (allowedSubcontractIds !== "all" && Array.isArray(allowedSubcontractIds)) {
        subcontractParams.contractIds = allowedSubcontractIds.join(",");
      }

      const [mainContract, mainPaymentsResult, subcontractResult] = await Promise.all([
        selectedMainContract
          ? Promise.resolve(selectedMainContract)
          : safely(() => subcontractApi.getMainContract(contractId)),
        optionalRequest(() => subcontractApi.getMainContractPayments(contractId), []),
        safely(() => subcontractApi.getSubcontracts(subcontractParams)),
      ]);

      const subcontracts = (subcontractResult.data || []).filter(
        (subcontract) => String(getField(subcontract, "contractId")) === String(contractId),
      );

      const details = await Promise.all(
        subcontracts.map(async (subcontract) => {
          const [payments, acceptances] = await Promise.all([
            safely(() => subcontractApi.getPaymentProfiles(getField(subcontract, "subcontractId"))),
            safely(() => subcontractApi.getAcceptanceProfiles(getField(subcontract, "subcontractId"))),
          ]);
          const paymentItems = getItemList(payments);
          const acceptanceItems = getItemList(acceptances);

          return {
            ...subcontract,
            paidAmount: sumBy(paymentItems, "paymentAmount"),
            acceptedQuantity: sumBy(acceptanceItems, "acceptedQuantity"),
            subcontractQuantity: sumBy(acceptanceItems, "subcontractQuantity"),
            rejectedQuantity: sumBy(acceptanceItems, "rejectedQuantity"),
            remainingQuantity: sumBy(acceptanceItems, "remainingQuantity"),
          };
        }),
      );

      setData({
        mainContract,
        mainContractPaid: sumBy(mainPaymentsResult, "paymentAmount"),
        subcontracts: details,
      });
      setError("");
    } catch (requestError) {
      setData(null);
      setError(requestError.response?.data?.message || requestError.message || "Không thể tải dữ liệu thống kê.");
    } finally {
      setLoading(false);
    }
  }, [contractId, selectedMainContract, allowedSubcontractIds]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const summary = useMemo(() => {
    const subcontracts = data?.subcontracts || [];
    const mainTotal = toNumber(getField(data?.mainContract, "totalValue"));
    const mainPaid = toNumber(data?.mainContractPaid);
    const subTotal = sumBy(subcontracts, "totalValue");
    const subPaid = sumBy(subcontracts, "paidAmount");

    return {
      totalSubcontracts: subcontracts.length,
      inProgress: subcontracts.filter((item) => statusEquals(getField(item, "status"), "Đang thực hiện")).length,
      completed: subcontracts.filter((item) => statusEquals(getField(item, "status"), "Hoàn thành")).length,
      mainTotal,
      mainPaid,
      mainRemaining: Math.max(mainTotal - mainPaid, 0),
      subTotal,
      subPaid,
      subRemaining: Math.max(subTotal - subPaid, 0),
    };
  }, [data]);

  if (!contractId) {
    return <Alert severity="info">Vui lòng chọn hợp đồng chính để xem thống kê.</Alert>;
  }

  if (loading || allowedSubcontractIds === null) {
    return (
      <Box className="subcontract-stat-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data?.mainContract) {
    return <Alert severity="warning">Không tìm thấy thông tin hợp đồng chính.</Alert>;
  }

  return (
    <Box className="subcontract-statistics">
      <SectionCard
        // title={`${getField(data.mainContract, "contractNumber")} - ${getField(data.mainContract, "contractName")}`}
        // subtitle="Thống kê theo hợp đồng chính và các quyết định giao khoán thuộc hợp đồng này"
        title={`${getField(data.mainContract, "contractNumber")}`}
        subtitle={`${getField(data.mainContract, "contractName")}`}
      >
        <Box className="subcontract-stat-parent">
          <Box className="subcontract-stat-parent__label">
            <AssignmentOutlined fontSize="small" />
            <span>Thông tin hợp đồng chính</span>
          </Box>

          <Box className="subcontract-stat-summary-grid">
            <SummaryCard
              icon={<AssignmentOutlined fontSize="small" />}
              title="Số lượng giao khoán"
              rows={[
                { label: "Tổng số giao khoán", value: formatNumber(summary.totalSubcontracts) },
                { label: "Đang thực hiện", value: formatNumber(summary.inProgress) },
                { label: "Hoàn thành", value: formatNumber(summary.completed) },
              ]}
            />
            <SummaryCard
              icon={<AccountBalanceWalletOutlined fontSize="small" />}
              title="Thanh toán hợp đồng chính"
              rows={[
                { label: "Giá trị hợp đồng", value: formatCurrencyWithUnit(summary.mainTotal) },
                { label: "Đã thanh toán", value: formatCurrencyWithUnit(summary.mainPaid) },
                { label: "Còn lại", value: formatCurrencyWithUnit(summary.mainRemaining) },
              ]}
            />
            <SummaryCard
              icon={<PaymentsOutlined fontSize="small" />}
              title="Thanh toán tất cả giao khoán"
              rows={[
                { label: "Giá trị giao khoán", value: formatCurrencyWithUnit(summary.subTotal) },
                { label: "Đã thanh toán", value: formatCurrencyWithUnit(summary.subPaid) },
                { label: "Còn lại", value: formatCurrencyWithUnit(summary.subRemaining) },
              ]}
            />
          </Box>
        </Box>

        <Box className="subcontract-stat-children">
          <Box className="subcontract-stat-children__header">
            <Box>
              <Typography className="subcontract-stat-children__title">Danh sách quyết định giao khoán</Typography>
              {/* <Typography className="subcontract-stat-children__subtitle">
                Các quyết định giao khoán thuộc hợp đồng chính đang chọn
              </Typography> */}
            </Box>
            <span>{formatNumber(summary.totalSubcontracts)} giao khoán</span>
          </Box>

          <Box className="subcontract-stat-list">
            {data.subcontracts.length === 0 ? (
              <Alert severity="info">Hợp đồng chính này chưa có quyết định giao khoán.</Alert>
            ) : (
              data.subcontracts.map((item) => (
                <SubcontractStatCard key={getField(item, "subcontractId")} item={item} />
              ))
            )}
          </Box>
        </Box>
      </SectionCard>
    </Box>
  );
}
